import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlayCircle, Zap, ShieldCheck, CheckCircle2, Sparkles, Search, DollarSign, FileText, Users } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatMoney } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const BUNDLES = [
  { key: "skip_trace",             label: "Skip Trace",             brokers: 3, cost_cents: 3600,  desc: "Address + skip-trace essentials." },
  { key: "employment_bank",        label: "Employment + Bank",      brokers: 4, cost_cents: 5400,  desc: "Wage garnishment prep — employer + institution surface." },
  { key: "full_asset_sweep",       label: "Full Asset Sweep",       brokers: 6, cost_cents: 8200,  desc: "Full portfolio search across identity, property, and courts." },
  { key: "comprehensive_discovery",label: "Comprehensive Discovery",brokers: 7, cost_cents: 12250, desc: "Every broker — highest-conviction result." },
];

const DemoRunQueryPage = () => {
  const demo = useDemo();
  const [debtors, setDebtors] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [recentRuns, setRecentRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [debtorId, setDebtorId] = useState(null);
  const [bundle, setBundle] = useState("full_asset_sweep");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, b, r, s, rep] = await Promise.all([
          demoClient.get("/demo/debtors"),
          demoClient.get("/demo/brokers"),
          demoClient.get("/demo/queries/recent"),
          demoClient.get("/demo/queries/stats"),
          demoClient.get("/demo/reports"),
        ]);
        setDebtors(d.data.debtors || []);
        setBrokers(b.data.brokers || []);
        setRecentRuns(r.data.queries || []);
        setStats(s.data);
        setReports(rep.data.reports || []);
        if (d.data.debtors?.length) setDebtorId(d.data.debtors[0].id);
      } finally { setLoading(false); }
    })();
  }, []);

  const nudge = () => demo?.openSignUp("running queries");
  const selected = BUNDLES.find((x) => x.key === bundle);
  const selectedDebtor = debtors.find((d) => d.id === debtorId);

  // Derive stats from recent runs (they carry cost + timestamps)
  const thisMonth = React.useMemo(() => {
    const now = new Date();
    return recentRuns.filter((r) => {
      const t = new Date(r.started_at || r.completed_at);
      return t.getMonth() === now.getMonth() && t.getFullYear() === now.getFullYear();
    });
  }, [recentRuns]);
  const spendThisMonth = thisMonth.reduce((acc, r) => acc + (r.total_cost_cents || 0), 0);
  const debtorNameById = React.useMemo(() => {
    const m = new Map();
    debtors.forEach((d) => m.set(d.id, d.full_name));
    return m;
  }, [debtors]);

  const bundleLabel = (b) => ({
    skip_trace: "Skip Trace",
    employment_bank: "Employment + Bank",
    full_asset_sweep: "Full Asset Sweep",
    comprehensive_discovery: "Comprehensive Discovery",
  }[b] || (b || "").replace(/_/g, " "));

  const shortDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return "—"; }
  };

  return (
    <div className="space-y-6" data-testid="demo-run-query-page">
      <div>
        <div className="jea-caption">Broker Orchestration</div>
        <h1 className="text-[28px] font-bold text-white">Run Query</h1>
        <p className="text-text-secondary text-[14px] mt-1">
          One click orchestrates every broker against a debtor — real-time intelligence in seconds.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="demo-runquery-stats">
        <StatTile
          label="Queries This Month"
          value={stats ? thisMonth.length : "—"}
          Icon={Search}
        />
        <StatTile
          label="Spend This Month"
          value={formatMoney(spendThisMonth)}
          Icon={DollarSign}
          money
        />
        <StatTile
          label="Reports Generated"
          value={stats?.reports_total ?? reports.length}
          Icon={FileText}
        />
        <StatTile
          label="Judgments Ready"
          value={debtors.length}
          Icon={Users}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6" data-tour="run-query-panel">
        <div className="space-y-6">
          {/* Debtor selector */}
          <JeaCard>
            <div className="jea-caption mb-3">Step 1 · Choose Judgment</div>
            {loading ? <Skeleton className="h-24" /> : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                {debtors.slice(0, 6).map((d) => {
                  const active = d.id === debtorId;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setDebtorId(d.id)}
                      className={cn(
                        "text-left rounded-[8px] border p-3 transition-all",
                        active
                          ? "border-accent-pink bg-accent-pink/10"
                          : "border-border-default hover:border-accent-pink/40 hover:bg-bg-tertiary"
                      )}
                      data-testid={`demo-runquery-debtor-${d.id}`}
                    >
                      <div className="text-white text-[13px] font-semibold truncate">{d.full_name}</div>
                      <div className="jea-caseno text-[11px]">{d.judgment?.case_number || "—"}</div>
                      <div className="jea-mono text-accent-pink font-bold text-[13px] mt-1">
                        {formatMoney(d.judgment?.amount_cents || 0)}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </JeaCard>

          {/* Bundle */}
          <JeaCard>
            <div className="jea-caption mb-3">Step 2 · Choose Bundle</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {BUNDLES.map((b) => {
                const active = b.key === bundle;
                return (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => setBundle(b.key)}
                    className={cn(
                      "text-left rounded-[8px] border p-4 transition-all",
                      active
                        ? "border-accent-pink bg-accent-pink/10"
                        : "border-border-default hover:border-accent-pink/40 hover:bg-bg-tertiary"
                    )}
                    data-testid={`demo-bundle-${b.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white text-[14px] font-semibold">{b.label}</span>
                      <span className="jea-mono text-accent-pink font-bold text-[13px]">
                        {formatMoney(b.cost_cents)}
                      </span>
                    </div>
                    <div className="text-text-secondary text-[12px] mt-1">{b.desc}</div>
                    <div className="text-[11px] jea-caption mt-2">{b.brokers} BROKERS</div>
                  </button>
                );
              })}
            </div>
          </JeaCard>

          {/* Broker list */}
          <JeaCard>
            <div className="jea-caption mb-3">Included Data Sources</div>
            {loading ? <Skeleton className="h-24" /> : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {brokers.slice(0, selected?.brokers || 6).map((b) => (
                  <div
                    key={b.slug}
                    className="flex items-center gap-2 bg-bg-tertiary rounded-[8px] p-3"
                    data-testid={`demo-broker-tile-${b.slug}`}
                  >
                    <CheckCircle2 size={14} className="text-success shrink-0" />
                    <div className="min-w-0">
                      <div className="text-white text-[12px] font-semibold truncate">{b.name}</div>
                      <div className="text-text-muted text-[11px] truncate">{b.category}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </JeaCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <JeaCard data-testid="demo-runquery-summary">
            <div className="jea-caption mb-1">Ready to Run</div>
            <div className="text-white text-[16px] font-bold">{selectedDebtor?.full_name || "—"}</div>
            <div className="text-text-secondary text-[13px] mt-1">{selected?.label}</div>
            <div className="jea-mono text-accent-pink font-extrabold text-[26px] mt-3">
              {formatMoney(selected?.cost_cents || 0)}
            </div>
            <div className="text-text-muted text-[12px]">{selected?.brokers} brokers · &lt; 45s</div>
            <JeaButton
              className="mt-4 gap-2"
              fullWidth
              onClick={nudge}
              data-testid="demo-runquery-cta"
            >
              <PlayCircle size={16} /> Run Query
            </JeaButton>
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-text-secondary text-[12px]">
                <Zap size={12} className="text-accent-pink" /> Parallel dispatch across all brokers
              </div>
              <div className="flex items-center gap-2 text-text-secondary text-[12px]">
                <ShieldCheck size={12} className="text-accent-pink" /> Bankruptcy check auto-runs first
              </div>
            </div>
          </JeaCard>

          <JeaCard>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-accent-pink" />
              <div className="jea-caption">Why Bundle?</div>
            </div>
            <p className="text-text-secondary text-[12px]">
              Every additional broker cross-validates the last. Confidence goes up. Missed
              assets go down. 5+ brokers unlocks 15% bundle discount.
            </p>
          </JeaCard>
        </div>
      </div>

      {/* Recent Query Runs */}
      <JeaCard className="p-0 overflow-hidden" data-testid="demo-runquery-recent">
        <div className="p-6 pb-3 flex items-center justify-between">
          <h2 className="text-white font-bold text-[16px]">Recent Query Runs</h2>
          <Link to="/demo/reports" className="text-accent-pink text-[13px] font-semibold hover:text-accent-pink-light">
            View all reports →
          </Link>
        </div>
        {loading ? (
          <div className="p-6 pt-2"><Skeleton className="h-32" /></div>
        ) : recentRuns.length === 0 ? (
          <div className="p-8 text-text-secondary text-[13px] text-center">
            No queries yet. Sign up to run your first.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-t border-border-default">
                {["Judgment", "Bundle", "Brokers", "Cost", "Status", "Started"].map((h) => (
                  <th key={h} className="jea-caption text-text-secondary p-4 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentRuns.slice(0, 10).map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-border-default hover:bg-bg-tertiary transition-colors"
                  data-testid={`demo-runquery-row-${r.id}`}
                >
                  <td className="p-4 text-white text-[13px] font-semibold">
                    {debtorNameById.get(r.debtor_id) || "—"}
                  </td>
                  <td className="p-4 text-text-secondary text-[13px]">{bundleLabel(r.bundle)}</td>
                  <td className="p-4 jea-mono text-text-secondary text-[13px]">{r.broker_count}</td>
                  <td className="p-4 jea-mono text-accent-pink font-bold">{formatMoney(r.total_cost_cents || 0)}</td>
                  <td className="p-4">
                    <span className={cn(
                      "inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.08em] uppercase",
                      r.status === "completed" ? "bg-success/15 text-success" :
                      r.status === "running"   ? "bg-warning/15 text-warning" :
                      "bg-bg-tertiary text-text-secondary"
                    )}>
                      {r.status || "completed"}
                    </span>
                  </td>
                  <td className="p-4 jea-date">{shortDate(r.started_at || r.completed_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </JeaCard>
    </div>
  );
};

const StatTile = ({ label, value, Icon, money }) => (
  <JeaCard>
    <div className="flex items-center justify-between">
      <div className="jea-caption">{label}</div>
      {Icon && <Icon size={14} className="text-text-muted" />}
    </div>
    <div className={cn(
      "jea-mono font-extrabold text-[24px] mt-2",
      money ? "text-accent-pink" : "text-white"
    )}>
      {value}
    </div>
  </JeaCard>
);

export default DemoRunQueryPage;
